import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, FileCheck, Users, BarChart3, Lock, FlaskConical, Cpu, Globe, Database, AlertTriangle, Clock, Zap, Briefcase, KeyRound, Eye, EyeOff } from 'lucide-react'

// ── Microsoft official logo ──────────────────────────────────────────
const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect x="1"  y="1"  width="9" height="9" fill="#f25022" />
    <rect x="11" y="1"  width="9" height="9" fill="#7fba00" />
    <rect x="1"  y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
)

// ── Animated background canvas (floating particles) ──────────────────
function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animId: number
    const BRAND = '11, 188, 193'

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // Particles
    const particles = Array.from({ length: 40 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.3,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.4 + 0.05,
    }))

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      for (const p of particles) {
        p.x += p.vx
        p.y += p.vy
        if (p.x < 0) p.x = canvas.width
        if (p.x > canvas.width) p.x = 0
        if (p.y < 0) p.y = canvas.height
        if (p.y > canvas.height) p.y = 0
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(${BRAND}, ${p.alpha})`
        ctx.fill()
      }
      // Connection lines between close particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < 120) {
            ctx.beginPath()
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.strokeStyle = `rgba(${BRAND}, ${0.06 * (1 - dist / 120)})`
            ctx.lineWidth = 0.5
            ctx.stroke()
          }
        }
      }
      animId = requestAnimationFrame(draw)
    }
    draw()

    return () => {
      cancelAnimationFrame(animId)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.8 }}
    />
  )
}

// ── Feature pill (floating around the card) ──────────────────────────
function FeaturePill({
  icon: Icon,
  label,
  style,
}: {
  icon: React.ElementType
  label: string
  style: React.CSSProperties
}) {
  return (
    <div
      className="fixed hidden lg:flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium select-none pointer-events-none"
      style={{
        background: 'rgba(10, 16, 24, 0.85)',
        border: '1px solid rgba(11, 188, 193, 0.12)',
        backdropFilter: 'blur(12px)',
        color: 'rgba(255,255,255,0.5)',
        ...style,
      }}
    >
      <Icon size={12} style={{ color: '#0BBCC1' }} />
      {label}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────
export default function LoginPage() {
  const navigate = useNavigate()
  const [devLoading, setDevLoading] = useState(false)
  const [devError, setDevError] = useState('')
  const [authMethods, setAuthMethods] = useState<string[]>([])
  const [stagingEmail, setStagingEmail] = useState('')
  const [stagingPassword, setStagingPassword] = useState('')
  const [stagingLoading, setStagingLoading] = useState(false)
  const [stagingError, setStagingError] = useState('')
  const [showStagingPassword, setShowStagingPassword] = useState(false)

  // Bouton dev visible si on est sur localhost (Docker dev ou npm run dev)
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const base = window.__RUNTIME_CONFIG__?.API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000'

  // Si déjà connecté → app
  useEffect(() => {
    if (sessionStorage.getItem('acodoc_token')) {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate])

  // Charger les méthodes d'auth disponibles depuis le backend
  useEffect(() => {
    fetch(`${base}/api/v1/auth/methods`)
      .then(r => r.json())
      .then(data => setAuthMethods(data.methods ?? []))
      .catch(() => setAuthMethods([]))
  }, [base])

  const handleLogin = () => {
    window.location.href = `${base}/api/v1/auth/login`
  }

  const handleDevLogin = async () => {
    setDevLoading(true)
    setDevError('')
    try {
      const res = await fetch(`${base}/api/v1/auth/dev-login`, { method: 'POST' })
      if (!res.ok) throw new Error()
      const { access_token } = await res.json()
      sessionStorage.setItem('acodoc_token', access_token)
      navigate('/dashboard', { replace: true })
    } catch {
      setDevError('Erreur de connexion dev. Vérifier que l\'API tourne.')
    } finally {
      setDevLoading(false)
    }
  }

  const handleStagingLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setStagingLoading(true)
    setStagingError('')
    try {
      const res = await fetch(`${base}/api/v1/auth/staging-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: stagingEmail, password: stagingPassword }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Identifiants incorrects.')
      }
      const { access_token } = await res.json()
      sessionStorage.setItem('acodoc_token', access_token)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setStagingError(err instanceof Error ? err.message : 'Erreur de connexion.')
    } finally {
      setStagingLoading(false)
    }
  }

  return (
    <>
      {/* ── CSS Keyframes ─────────────────────────────────────────── */}
      <style>{`
        @keyframes orb-drift-1 {
          0%,100% { transform: translate(0,0) scale(1); }
          33%      { transform: translate(60px,-80px) scale(1.08); }
          66%      { transform: translate(-40px, 40px) scale(0.94); }
        }
        @keyframes orb-drift-2 {
          0%,100% { transform: translate(0,0) scale(1); }
          50%      { transform: translate(-80px,-60px) scale(1.12); }
        }
        @keyframes orb-drift-3 {
          0%,100% { transform: translate(0,0) scale(1); }
          40%      { transform: translate(40px, 100px) scale(0.9); }
          80%      { transform: translate(-60px,-20px) scale(1.06); }
        }
        @keyframes card-enter {
          from { opacity:0; transform:translateY(32px) scale(0.97); }
          to   { opacity:1; transform:translateY(0)    scale(1); }
        }
        @keyframes pill-enter {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes border-glow {
          0%,100% { box-shadow: 0 0 0 1px rgba(11,188,193,0.15), 0 24px 80px rgba(0,0,0,0.6); }
          50%      { box-shadow: 0 0 0 1px rgba(11,188,193,0.30), 0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(11,188,193,0.06); }
        }
        @keyframes shimmer-slide {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes badge-pulse {
          0%,100% { opacity:1; }
          50%      { opacity:0.4; }
        }
        @keyframes float-pill-1 {
          0%,100% { transform:translateY(0) rotate(-1deg); }
          50%      { transform:translateY(-7px) rotate(0deg); }
        }
        @keyframes float-pill-2 {
          0%,100% { transform:translateY(0) rotate(1deg); }
          50%      { transform:translateY(-9px) rotate(0deg); }
        }
        @keyframes float-pill-3 {
          0%,100% { transform:translateY(0) rotate(-0.5deg); }
          50%      { transform:translateY(-6px) rotate(0.5deg); }
        }
        @keyframes float-pill-4 {
          0%,100% { transform:translateY(0) rotate(1deg); }
          50%      { transform:translateY(-8px) rotate(0deg); }
        }
        @keyframes float-pill-5 {
          0%,100% { transform:translateY(0) rotate(-1deg); }
          50%      { transform:translateY(-10px) rotate(0.5deg); }
        }
        @keyframes float-pill-6 {
          0%,100% { transform:translateY(0) rotate(0.5deg); }
          50%      { transform:translateY(-6px) rotate(-0.5deg); }
        }
        @keyframes float-pill-7 {
          0%,100% { transform:translateY(0) rotate(-1deg); }
          50%      { transform:translateY(-8px) rotate(1deg); }
        }
        @keyframes float-pill-8 {
          0%,100% { transform:translateY(0) rotate(0deg); }
          50%      { transform:translateY(-11px) rotate(-0.5deg); }
        }
        @keyframes float-pill-9 {
          0%,100% { transform:translateY(0) rotate(0.5deg); }
          50%      { transform:translateY(-9px) rotate(-1deg); }
        }
        @keyframes float-pill-10 {
          0%,100% { transform:translateY(0) rotate(-0.5deg); }
          50%      { transform:translateY(-7px) rotate(0.5deg); }
        }
        @keyframes float-pill-11 {
          0%,100% { transform:translateY(0) rotate(1deg); }
          50%      { transform:translateY(-10px) rotate(0deg); }
        }
        @keyframes float-pill-12 {
          0%,100% { transform:translateY(0) rotate(-1deg); }
          50%      { transform:translateY(-8px) rotate(0.5deg); }
        }

        .orb-1 { animation: orb-drift-1 18s ease-in-out infinite; }
        .orb-2 { animation: orb-drift-2 22s ease-in-out infinite; }
        .orb-3 { animation: orb-drift-3 15s ease-in-out infinite; }

        .card-animate {
          animation: card-enter 0.8s cubic-bezier(0.22,1,0.36,1) forwards,
                     border-glow 5s ease-in-out 0.8s infinite;
        }

        .shimmer-btn {
          background: linear-gradient(
            110deg,
            #0BBCC1 0%,
            #0BBCC1 35%,
            #7ef7fa 50%,
            #0BBCC1 65%,
            #0BBCC1 100%
          );
          background-size: 250% auto;
          transition: background-position 0s, transform 0.15s, box-shadow 0.15s;
        }
        .shimmer-btn:hover {
          animation: shimmer-slide 1.4s linear infinite;
          transform: translateY(-1px) scale(1.01);
          box-shadow: 0 8px 32px rgba(11,188,193,0.35), 0 0 0 1px rgba(11,188,193,0.4);
        }
        .shimmer-btn:active {
          transform: scale(0.98);
          animation: none;
        }

        .dot-grid {
          background-image: radial-gradient(circle, rgba(11,188,193,0.12) 1px, transparent 1px);
          background-size: 32px 32px;
        }
      `}</style>

      <div className="relative min-h-screen overflow-hidden flex items-center justify-center" style={{ background: '#050c14' }}>

        {/* ── Dot grid ─────────────────────────────────────────────── */}
        <div className="dot-grid absolute inset-0" />

        {/* ── Particle canvas ──────────────────────────────────────── */}
        <ParticleCanvas />

        {/* ── Ambient orbs ─────────────────────────────────────────── */}
        <div className="orb-1 absolute top-[15%] left-[12%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(11,188,193,0.12) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="orb-2 absolute bottom-[10%] right-[10%] w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(11,188,193,0.08) 0%, transparent 70%)', filter: 'blur(70px)' }} />
        <div className="orb-3 absolute top-[50%] right-[20%] w-[300px] h-[300px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(11,188,193,0.06) 0%, transparent 70%)', filter: 'blur(50px)' }} />

        {/* ── Radial vignette ──────────────────────────────────────── */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 80% 80% at 50% 50%, transparent 40%, rgba(5,12,20,0.7) 100%)' }} />

        {/* ── Floating feature pills ────────────────────────────────── */}
        <div className="relative z-10 w-full max-w-md mx-4">
          {/* Gauche — dispersées verticalement sur tout l'écran */}
          <FeaturePill icon={Zap}           label="Traitement OCR"       style={{ top:  '5vh',  left: '2%',  animation: 'pill-enter 0.5s 0.8s both, float-pill-9  7.0s 1.3s ease-in-out infinite' }} />
          <FeaturePill icon={FileCheck}     label="Classification IA"    style={{ top: '19vh',  left: '1%',  animation: 'pill-enter 0.5s 1.0s both, float-pill-1  6.0s 1.5s ease-in-out infinite' }} />
          <FeaturePill icon={Shield}        label="Conformité RGPD"      style={{ top: '34vh',  left: '3%',  animation: 'pill-enter 0.5s 1.2s both, float-pill-2  7.5s 1.7s ease-in-out infinite' }} />
          <FeaturePill icon={AlertTriangle} label="Alertes RGPD"         style={{ top: '50vh',  left: '1%',  animation: 'pill-enter 0.5s 1.4s both, float-pill-5  8.0s 1.9s ease-in-out infinite' }} />
          <FeaturePill icon={Globe}         label="Multi-langues"        style={{ top: '65vh',  left: '3%',  animation: 'pill-enter 0.5s 1.6s both, float-pill-6  5.5s 2.1s ease-in-out infinite' }} />
          <FeaturePill icon={Clock}         label="Rétention RGPD"       style={{ top: '80vh',  left: '2%',  animation: 'pill-enter 0.5s 1.8s both, float-pill-10 6.5s 2.3s ease-in-out infinite' }} />
          {/* Droite — symétriques */}
          <FeaturePill icon={Briefcase}     label="Documents administratifs"         style={{ top:  '5vh',  right: '2%', animation: 'pill-enter 0.5s 0.9s both, float-pill-11 8.0s 1.4s ease-in-out infinite' }} />
          <FeaturePill icon={Users}         label="Gestion RH"           style={{ top: '19vh',  right: '1%', animation: 'pill-enter 0.5s 1.1s both, float-pill-3  5.0s 1.6s ease-in-out infinite' }} />
          <FeaturePill icon={Cpu}           label="IA souveraine"        style={{ top: '34vh',  right: '3%', animation: 'pill-enter 0.5s 1.3s both, float-pill-7  6.5s 1.8s ease-in-out infinite' }} />
          <FeaturePill icon={BarChart3}     label="Analytiques"          style={{ top: '50vh',  right: '1%', animation: 'pill-enter 0.5s 1.5s both, float-pill-4  7.0s 2.0s ease-in-out infinite' }} />
          <FeaturePill icon={Database}      label="Base souveraine"      style={{ top: '65vh',  right: '3%', animation: 'pill-enter 0.5s 1.7s both, float-pill-8  9.0s 2.2s ease-in-out infinite' }} />
          <FeaturePill icon={Lock}          label="Accès sécurisé"       style={{ top: '80vh',  right: '2%', animation: 'pill-enter 0.5s 1.9s both, float-pill-12 7.5s 2.4s ease-in-out infinite' }} />

          {/* ── Card ───────────────────────────────────────────────── */}
          <div
            className="card-animate relative rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(145deg, rgba(13,20,30,0.95) 0%, rgba(8,14,22,0.98) 100%)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(11,188,193,0.18)',
            }}
          >
            {/* Top shimmer line */}
            <div className="absolute top-0 inset-x-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(11,188,193,0.6) 40%, rgba(11,188,193,0.8) 50%, rgba(11,188,193,0.6) 60%, transparent 100%)' }} />

            {/* Inner glow top-left */}
            <div className="absolute -top-20 -left-20 w-40 h-40 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(11,188,193,0.08) 0%, transparent 70%)' }} />

            <div className="px-10 py-12">

              {/* Logo dominant */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  {/* Halo large derrière le logo */}
                  <div className="absolute inset-0 -mx-8 -my-4"
                    style={{
                      background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(11,188,193,0.1) 0%, transparent 70%)',
                      filter: 'blur(20px)',
                    }} />
                  <img
                    src="/assets/ACO_All_Black_Top.png"
                    alt="Alcyone Consulting"
                    className="relative h-40 w-auto"
                    style={{ filter: 'brightness(0) invert(1) drop-shadow(0 0 28px rgba(11,188,193,0.4))' }}
                  />
                </div>
              </div>

              {/* Badge */}
              <div className="flex justify-center mb-5">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full"
                  style={{
                    background: 'rgba(11,188,193,0.08)',
                    border: '1px solid rgba(11,188,193,0.22)',
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: '#0BBCC1', animation: 'badge-pulse 2s ease-in-out infinite' }}
                  />
                  <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#0BBCC1' }}>
                    IA Souveraine
                  </span>
                </div>
              </div>

              {/* Title */}
              <div className="text-center mb-10">
                <h1 className="text-4xl font-bold tracking-tight mb-3"
                  style={{
                    color: '#fff',
                    letterSpacing: '-0.02em',
                    textShadow: '0 0 40px rgba(11,188,193,0.15)',
                  }}
                >
                  ACODOC
                </h1>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Classifiez, extrayez et sécurisez<br />
                  vos documents administratifs.
                </p>
              </div>

              {/* Separator */}
              <div className="flex items-center gap-4 mb-7">
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
                <div className="flex items-center gap-1.5">
                  <Lock size={10} style={{ color: 'rgba(255,255,255,0.2)' }} />
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>Connexion sécurisée</span>
                </div>
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
              </div>

              {/* Microsoft SSO Button */}
              <button
                onClick={handleLogin}
                className="shimmer-btn w-full flex items-center justify-center gap-3 py-4 px-6 rounded-2xl font-semibold text-sm text-white cursor-pointer"
                style={{ boxShadow: '0 4px 24px rgba(11,188,193,0.2), 0 0 0 1px rgba(11,188,193,0.25)' }}
              >
                <MicrosoftLogo />
                Se connecter avec Microsoft
              </button>

              {/* ── Mode staging (login email + mot de passe) ── */}
              {authMethods.includes('staging') && (
                <div className="mt-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
                    <span className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(251,146,60,0.7)' }}>
                      <KeyRound size={11} />
                      accès staging
                    </span>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
                  </div>

                  <form onSubmit={handleStagingLogin} className="flex flex-col gap-2">
                    <input
                      type="email"
                      placeholder="Email admin"
                      value={stagingEmail}
                      onChange={e => setStagingEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(251,146,60,0.2)',
                        color: 'rgba(255,255,255,0.8)',
                      }}
                    />
                    <div className="relative">
                      <input
                        type={showStagingPassword ? 'text' : 'password'}
                        placeholder="Mot de passe"
                        value={stagingPassword}
                        onChange={e => setStagingPassword(e.target.value)}
                        required
                        className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none"
                        style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(251,146,60,0.2)',
                          color: 'rgba(255,255,255,0.8)',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowStagingPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded"
                        style={{ color: 'rgba(251,146,60,0.6)' }}
                        tabIndex={-1}
                      >
                        {showStagingPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <button
                      type="submit"
                      disabled={stagingLoading}
                      className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-2xl text-sm font-medium transition-all duration-200 hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: 'rgba(251,146,60,0.08)',
                        border: '1px solid rgba(251,146,60,0.25)',
                        color: 'rgba(251,146,60,0.9)',
                      }}
                    >
                      <KeyRound size={14} />
                      {stagingLoading ? 'Connexion…' : 'Connexion Admin'}
                    </button>
                  </form>

                  {stagingError && (
                    <p className="mt-2 text-center text-xs" style={{ color: 'rgba(239,68,68,0.8)' }}>
                      {stagingError}
                    </p>
                  )}
                </div>
              )}

              {/* ── Mode développement (visible uniquement en dev) ── */}
              {isDev && (
                <div className="mt-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
                    <span className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(251,191,36,0.6)' }}>
                      <FlaskConical size={11} />
                      dev only
                    </span>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
                  </div>

                  <button
                    onClick={handleDevLogin}
                    disabled={devLoading}
                    className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-2xl text-sm font-medium transition-all duration-200 hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: 'rgba(251,191,36,0.06)',
                      border: '1px solid rgba(251,191,36,0.2)',
                      color: 'rgba(251,191,36,0.8)',
                    }}
                  >
                    <FlaskConical size={14} />
                    {devLoading ? 'Connexion…' : 'Connexion Admin (dev)'}
                  </button>

                  {devError && (
                    <p className="mt-2 text-center text-xs" style={{ color: 'rgba(239,68,68,0.8)' }}>
                      {devError}
                    </p>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="mt-7 flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(11,188,193,0.5)' }} />
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
                    Réservé aux managers
                  </span>
                  <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(11,188,193,0.5)' }} />
                </div>
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.12)' }}>
                  ACO © 2026
                </span>
              </div>
            </div>

            {/* Bottom shimmer line */}
            <div className="absolute bottom-0 inset-x-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(11,188,193,0.15), transparent)' }} />
          </div>
        </div>

        {/* ── Corner decorations ──────────────────────────────────── */}
        <div className="absolute top-8 left-8 w-12 h-12 pointer-events-none hidden lg:block"
          style={{
            borderTop: '1px solid rgba(11,188,193,0.2)',
            borderLeft: '1px solid rgba(11,188,193,0.2)',
            borderRadius: '4px 0 0 0',
          }} />
        <div className="absolute bottom-8 right-8 w-12 h-12 pointer-events-none hidden lg:block"
          style={{
            borderBottom: '1px solid rgba(11,188,193,0.2)',
            borderRight: '1px solid rgba(11,188,193,0.2)',
            borderRadius: '0 0 4px 0',
          }} />
      </div>
    </>
  )
}
